import { getNlsString } from '../../nls/nlsRuntime'
import type { NlsInterpolationValues } from '../../nls/nlsTypes'
import type { NlsKey } from '../../nls/strings_nls'
import type { ChecklistWorkflowStatus } from '../../types/schema'
import type { HelperTabKey } from '../../services/helper/helperStore'
import type { HelperControlsSubtab, HelperFieldSubtab } from '../Helper/helperFieldModel'
import type { ControlSection } from '../Member/memberControlsModel'
import type { MemberAiSubTab, MemberCollaborationSubTab, MemberTabKey } from '../../member/memberAppRouting'
import type { MemberBoardStatusFilter, MemberReviewReadiness } from '../Member/memberBoardUtils'

export type MobileNlsT = (key: NlsKey, values?: NlsInterpolationValues) => string

const defaultT: MobileNlsT = (key, values) => getNlsString(key, values)

const MEMBER_TAB_KEYS: Record<MemberTabKey, NlsKey> = {
  'for-you': 'NLSMemberTabTodos',
  board: 'NLSMemberTabBoard',
  ai: 'NLSMemberTabAiMagic',
  nexus: 'NLSMemberTabNexus',
  calendar: 'NLSMemberSubtabCalendar',
  chat: 'NLSMemberSubtabChat',
  controls: 'NLSMemberTabControls',
  you: 'NLSMemberTabYourPage',
  forms: 'NLSMemberTabForms',
  sync: 'NLSMemberTabSync',
  automations: 'NLSMemberTabAutomations',
  team: 'NLSMemberTabTeam',
  stock: 'NLSMemberTabStock',
  class: 'NLSMemberTabClass',
  bookings: 'NLSMemberTabBookings',
  ops: 'NLSMemberTabOperations',
}

const MEMBER_AI_SUBTAB_KEYS: Record<MemberAiSubTab, NlsKey> = {
  HISTORY: 'NLSMemberSubtabHistory',
  CREATE: 'NLSMemberSubtabCreate',
  EXECUTE: 'NLSMemberSubtabExecute',
  PULSE: 'NLSMemberSubtabPulse',
  MAGIC: 'NLSMemberSubtabMagic',
}

const MEMBER_COLLABORATION_SUBTAB_KEYS: Record<MemberCollaborationSubTab, NlsKey> = {
  CHAT: 'NLSMemberSubtabChat',
  CALENDAR: 'NLSMemberSubtabCalendar',
  HUDDLE: 'NLSMemberSubtabHuddle',
  MEETINGS: 'NLSMemberSubtabMeetings',
}

const MEMBER_CONTROL_KEYS: Record<ControlSection, NlsKey> = {
  AI: 'NLSMemberTabAiMagic',
  FORMS: 'NLSMemberSubtabForms',
  SYNC: 'NLSMemberSubtabSync',
  AUTOMATIONS: 'NLSMemberSubtabAutomations',
  MEMBERS: 'NLSMemberControlsSectionMembers',
  TIME: 'NLSMemberControlsSectionTime',
}

const MEMBER_STATUS_LABEL_KEYS: Record<ChecklistWorkflowStatus, NlsKey> = {
  CREATED: 'NLSMemberStatusCreated',
  ASSIGNED: 'NLSMemberStatusAssigned',
  ACCEPTED: 'NLSMemberStatusAccepted',
  ONGOING: 'NLSMemberStatusOngoing',
  ASSESSING: 'NLSMemberStatusAssessing',
  COMPLETED: 'NLSMemberStatusCompleted',
  NOT_REQUIRED: 'NLSMemberStatusNotRequired',
  POSTPONED: 'NLSMemberStatusPostponed',
}

const MEMBER_STATUS_HINT_KEYS: Record<ChecklistWorkflowStatus, NlsKey> = {
  CREATED: 'NLSMemberStatusCreatedHint',
  ASSIGNED: 'NLSMemberStatusAssignedHint',
  ACCEPTED: 'NLSMemberStatusAcceptedHint',
  ONGOING: 'NLSMemberStatusOngoingHint',
  ASSESSING: 'NLSMemberStatusAssessingHint',
  COMPLETED: 'NLSMemberStatusCompletedHint',
  NOT_REQUIRED: 'NLSMemberStatusNotRequiredHint',
  POSTPONED: 'NLSMemberStatusPostponedHint',
}

const MEMBER_REVIEW_KEYS: Record<MemberReviewReadiness, NlsKey> = {
  READY: 'NLSMemberStatusAccepted',
  EVIDENCE: 'NLSMemberBoardProofNeeded',
  LOCATION: 'NLSMemberBoardLocationNeeded',
  CO_SIGN: 'NLSMemberSubtabReview',
}

const HELPER_TAB_KEYS: Record<HelperTabKey, NlsKey> = {
  TASKS: 'NLSHelperTabTodos',
  CALENDAR: 'NLSHelperTabCalendar',
  MEETINGS: 'NLSHelperTabMeetings',
  CHAT: 'NLSHelperTabChat',
  FIELD: 'NLSHelperTabField',
  SCHOOL: 'NLSHelperTabSchool',
  OPS: 'NLSHelperTabWork',
}

const HELPER_FIELD_KEYS: Record<HelperFieldSubtab, NlsKey> = {
  TODAY: 'NLSHelperSubtabToday',
  SCAN: 'NLSHelperSubtabScan',
  COUNT: 'NLSHelperSubtabCount',
  ME: 'NLSHelperSubtabMe',
}

const HELPER_CONTROL_KEYS: Record<string, NlsKey> = {
  TODAY: 'NLSHelperSubtabToday',
  SCAN: 'NLSHelperSubtabScan',
  COUNT: 'NLSHelperSubtabCount',
  ME: 'NLSHelperSubtabMe',
  FORMS: 'NLSHelperSubtabForms',
  SYNC: 'NLSHelperSubtabSync',
}

export const getMemberTabLabel = (tab: MemberTabKey, t: MobileNlsT = defaultT) => t(MEMBER_TAB_KEYS[tab])

export const getMemberAiSubtabLabel = (tab: MemberAiSubTab, t: MobileNlsT = defaultT) =>
  t(MEMBER_AI_SUBTAB_KEYS[tab])

export const getMemberCollaborationSubtabLabel = (tab: MemberCollaborationSubTab, t: MobileNlsT = defaultT) =>
  t(MEMBER_COLLABORATION_SUBTAB_KEYS[tab])

export const getMemberControlSectionLabel = (section: ControlSection, t: MobileNlsT = defaultT) =>
  t(MEMBER_CONTROL_KEYS[section])

export const getMemberStatusLabel = (status: ChecklistWorkflowStatus, t: MobileNlsT = defaultT) =>
  t(MEMBER_STATUS_LABEL_KEYS[status])

export const getMemberStatusHint = (status: ChecklistWorkflowStatus, t: MobileNlsT = defaultT) =>
  t(MEMBER_STATUS_HINT_KEYS[status])

export const getMemberStatusFilterMeta = (status: MemberBoardStatusFilter, t: MobileNlsT = defaultT) =>
  status === 'ALL'
    ? { label: t('NLSMemberFilterAll'), hint: t('NLSMemberFilterAllHint') }
    : { label: getMemberStatusLabel(status, t), hint: getMemberStatusHint(status, t) }

export const getMemberReviewReadinessLabel = (readiness: MemberReviewReadiness, t: MobileNlsT = defaultT) =>
  t(MEMBER_REVIEW_KEYS[readiness])

export const getHelperTabLabel = (tab: HelperTabKey | 'CONTROLS' | 'SCHOOL' | string, t: MobileNlsT = defaultT) => {
  if (tab === 'CONTROLS') return t('NLSHelperTabControls')
  const upper = tab.toUpperCase() as HelperTabKey | 'SCHOOL'
  const key = HELPER_TAB_KEYS[upper]
  return key ? t(key) : tab
}

export const getHelperFieldSubtabNlsLabel = (subtab: HelperFieldSubtab, t: MobileNlsT = defaultT) =>
  t(HELPER_FIELD_KEYS[subtab])

export const getHelperControlsSubtabNlsLabel = (subtab: HelperControlsSubtab, t: MobileNlsT = defaultT) =>
  t(HELPER_CONTROL_KEYS[subtab])

export const getHelperFormCountLabel = (count: number, t: MobileNlsT = defaultT) =>
  count === 0
    ? t('NLSHelperFormsNoForms')
    : count === 1
      ? t('NLSHelperFormsCountOne', { count })
      : t('NLSHelperFormsCountMany', { count })
